import React from "react";
import styled from "styled-components";
import { Subtitle } from "./shared/Subtitle";
import { Title } from "./shared/Title";
import { ListItem } from "./shared/ListItem";
import { ScrollableImage } from "./shared/ScrollableImage";

const WrapperStyled = styled.div`
  margin: 70px auto 0;
  max-width: 1000px;
  padding: 0 5%;
  text-align: center;

  & p {
    padding: 10px 15%;
    text-align: left;
  }
`;

const GridRow1 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 30px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const GridRow2 = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 30px;
  margin-top: 80px;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 2fr;
    margin-top: 140px;

    & .narrow-img {
      order: 1;
    }

    & .text-project {
      order: 2;
    }
  }
`;

const ImageContainer = styled.div`
  display: grid;
  place-items: center;
  margin: 20px 0;

  & img {
    max-width: 100%;
  }
`;

const NarrowImg = styled.div`
  width: min(400px, 100%);
  height: 300px;
  margin: 30px auto 0;

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }

  @media (min-width: 1024px) {
    width: auto;
    height: auto;
    margin: 0;
  }
`;

const SubtitleAcademicP = styled(Subtitle)`
  text-align: left;
  margin: 0 0 30px 24px;
`;

const SubtitleProject = styled(SubtitleAcademicP)`
  @media (min-width: 1024px) {
    margin-top: -45px;
  }
`;

export const AcademicPlan = () => {
  return (
    <WrapperStyled>
      <Subtitle>Plan académico</Subtitle>
      <Title>
        En 6 meses estarás optando a empleos como frontend developer
      </Title>
      <p>
        En solo <b>6 meses</b> y en modalidad <b>part time</b> habrás aprendido
        todo lo necesario para trabajar como <b>frontend developer</b> en
        cualquier equipo de desarrollo.
        <br />
        <br />
        Además, conocerás todas las <b>dinámicas</b> de trabajo habituales en
        equipos profesionales de desarrollo: metodologías ágiles, ceremonías
        scrum, control de versiones, despliegues...
      </p>
      <ScrollableImage src="img/academic-plan.svg" />
      <GridRow1 style={{ marginTop: 70 }}>
        <div>
          <SubtitleAcademicP color="blue">
            Meses 1-5: PROGRAMA ACADÉMICO
          </SubtitleAcademicP>
          <ListItem title="Aprende de la mano de expertos en clases online dinámicas y 100% participativas">
            Vas a estar en primera fila, viendo cómo tu profesor resuelve
            ejercicios en tiempo real, compartiendo pantalla y guiándote paso a
            paso. No serás un espectador, sino parte activa de cada sesión.
            Pregunta, interactúa y resuelve dudas al instante. Así es como se
            aprende de verdad.
          </ListItem>
          <ListItem title="Aprende jugando: actividades grupales que te engancharán">
            Olvídate de las clases aburridas. Aquí, profundizarás en la materia
            a través de juegos y dinámicas en equipo que te harán disfrutar
            mientras aprendes. Conectarás con compañeros, compartirás ideas y te
            divertirás mientras adquieres habilidades clave.
          </ListItem>
        </div>
        <ImageContainer>
          <img src="/img/students.png" />
        </ImageContainer>
      </GridRow1>
      <GridRow1>
        <ListItem title="Practica, mejora y supera tus límites">
          La teoría es importante, pero la práctica es lo que te convertirá en
          un verdadero profesional. Con ejercicios y cuestionarios diseñados
          para desafiar tu mente, pondrás a prueba lo aprendido y ganarás la
          confianza necesaria para enfrentarte a cualquier proyecto. Cada
          ejercicio es un paso más hacia tu éxito.
        </ListItem>
        <ListItem title="Domina el futuro: escribe código con la ayuda de la IA">
          ¿Te imaginas poder optimizar tu trabajo y ser más eficiente que nunca?
          Con nosotros, no solo aprenderás a programar, sino también a
          aprovechar las herramientas más innovadoras de IA para potenciar tu
          productividad. Convierte los desafíos en oportunidades y destaca en el
          mundo tech.
        </ListItem>
      </GridRow1>
      <GridRow2>
        <div className="text-project">
          <SubtitleProject color="orange">
            Mes 6: PROYECTO FINAL
          </SubtitleProject>
          <ListItem
            iconColor="orange"
            title="Proyecto final grupal: tu primer paso hacia el mundo laboral"
          >
            En tu futuro como desarrollador, no estarás solo. Formarás parte de
            un equipo multidisciplinar donde{" "}
            <i>frontenders, backenders, UX/UI designers, scrum masters</i> y{" "}
            <i>product owners</i> trabajan juntos para crear algo
            extraordinario. Por eso, en Beyond, te preparamos para esa realidad.
          </ListItem>
          <ListItem iconColor="orange" title="¿Cómo lo hacemos?">
            Con un proyecto final grupal que simula un entorno laboral real. Te
            coordinarás con tus compañeros para desarrollar un producto de
            calidad, enfrentándote a los mismos desafíos que encontrarás en el
            sector IT.
            <br />
            <br />
            Los docentes actuarán como líderes de proyecto, guiándote con
            metodologías ágiles para que vivas la experiencia de trabajar en un
            equipo profesional.
          </ListItem>
          <ListItem
            iconColor="orange"
            title="Trabaja en sprints y vive las ceremonias Scrum"
          >
            Te sumergirás en la dinámica de un equipo <i>agile</i>, trabajando
            en sprints de 2 semanas y participando en todas las ceremonias
            clave: <i>sprint planning, sprint review</i> y 
            <i>sprint retrospectives</i>.
            <br />
            <br />
            Aprenderás a planificar, revisar y mejorar continuamente,
            adquiriendo habilidades que te harán indispensable en cualquier
            equipo.
          </ListItem>
        </div>
        <NarrowImg className="narrow-img">
          <img src="/img/last-month.png" />
        </NarrowImg>
      </GridRow2>
      <GridRow1 style={{ marginTop: 40 }}>
        <div>
          <ListItem
            iconColor="orange"
            title="Descubre el poder de la colaboración con Git y GitHub"
          >
            Aprenderás a usar estas herramientas esenciales para el desarrollo
            colaborativo. Git y GitHub no solo te ayudarán a controlar
            versiones, sino que te permitirán trabajar en equipo de manera
            eficiente y sin errores. Dominarás una de las habilidades más
            valoradas en el mundo tech.
          </ListItem>
          <ListItem iconColor="orange" title="Organiza tu trabajo con Kanban">
            Descubrirás cómo un tablero Kanban puede transformar la forma en que
            organizas y priorizas tareas, haciendo tu trabajo más fluido y
            visible. Esta herramienta te ayudará a mantener el control,
            optimizar tu flujo de trabajo y enfocarte en lo que realmente
            importa: crear soluciones innovadoras.
          </ListItem>
        </div>
        <ImageContainer>
          <img src="/img/post-its.png" />
        </ImageContainer>
      </GridRow1>
      <GridRow1>
        <div>
          <ListItem
            iconColor="orange"
            title="Diseña tus primeros prototipos con Figma y domina los fundamentos de UX/UI"
          >
            Te enseñamos a trabajar con Figma para aplicar conceptos básicos
            de UX/UI. Con estas habilidades, podrás crear prototipos funcionales
            y atractivos que servirán como base para tu proyecto final.
            Aprenderás a estructurar interfaces intuitivas, priorizar la
            experiencia del usuario y plasmar tus ideas de manera visual.
          </ListItem>
        </div>
        <div>
          <ListItem
            iconColor="orange"
            title="Este no es solo un proyecto, es tu puerta de entrada al mundo profesional"
          >
            Al finalizar, no solo habrás sido parte de la creación de un
            producto del que estar orgulloso, sino también la experiencia y la
            confianza para integrarte en cualquier equipo de desarrollo.
            ¡Prepárate para brillar!
          </ListItem>
        </div>
      </GridRow1>
    </WrapperStyled>
  );
};
